import React, { useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import { FormattedMessage } from '../../../util/reactIntl';
import { LISTING_CATEGORY, LISTING_TYPE } from '../../../constants/listing.constants';

import {
  Heading,
  IconArrowHead,
  InlineTextButton,
  Modal,
  SearchBar,
  SecondaryButtonInline,
} from '../../../components';
import CategoryFilter from '../../../components/SearchFilters/filters/CategoryFilter';
import BrandDropdown from '../../../components/SearchFilters/BrandDropdown/BrandDropdown';
import SecondaryFilters from '../SecondaryFilters/SecondaryFilters';

import css from './MainPanelHeader.module.css';

/**
 * MainPanelHeader component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {React.Node} props.children - The children
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.isSortByActive - Whether the sort by is active
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The results count
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {React.Node} props.noResultsInfo - The no results info
 * @returns {JSX.Element}
 */
const MainPanelHeader = props => {
  const location = useLocation();

  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress = false,
    noResultsInfo,
    renderPriceComponent,
    renderResetAllButton,
    onManageDisableScrolling,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={classes}>
      <div className={classNames(css.searchFilters, { [css.searchFiltersOpen]: isOpen })}>
        <div className={css.searchOptions}>
          <div>
            <Heading className={css.heading}>
              <FormattedMessage id="Global.boatsForSale" />
            </Heading>
          </div>
          <SearchBar
            className={css.searchbarRoot}
            inputClassName={css.searchbar}
            buttonClassName={css.searchbarButton}
            buttonContent={
              <span className={css.buttonContent}>
                <FormattedMessage id="Global.search" />
              </span>
            }
          />
        </div>
        <div className={css.filters}>
          <CategoryFilter
            categoryOptions={Object.values(LISTING_CATEGORY)}
            typeOptions={Object.values(LISTING_TYPE)}
          />
          <BrandDropdown />
          {renderPriceComponent()}
          {isOpen && <SecondaryFilters />}
        </div>
        {isOpen && (
          <SecondaryButtonInline className={css.closeFiltersBtn} onClick={() => setIsOpen(false)}>
            <span className={css.iconUp}>
              <IconArrowHead direction="up" />
              <IconArrowHead direction="up" />
            </span>
          </SecondaryButtonInline>
        )}
      </div>
      {!isOpen && (
        <InlineTextButton className={css.moreFilters} onClick={() => setIsOpen(true)}>
          <FormattedMessage id="Global.moreFilters" />
        </InlineTextButton>
      )}

      <div className={css.searchResultSummary}>
        {listingsAreLoaded && resultsCount > 0 && (
          <span className={css.searchResults}>
            <FormattedMessage id="MainPanelHeader.resultsFound" values={{ count: resultsCount }} />
          </span>
        )}
        {searchInProgress && (
          <span className={css.resultsFound}>
            <FormattedMessage id="MainPanelHeader.loadingResults" />
          </span>
        )}
        {isSortByActive ? (
          <div className={css.sortyByWrapper}>
            <span className={css.sortyBy}>
              <FormattedMessage id="MainPanelHeader.sortBy" />
            </span>
            {sortByComponent}
          </div>
        ) : null}
      </div>
      {renderResetAllButton()}

      {children}

      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

export default MainPanelHeader;
